import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, Box, Button, CircularProgress, InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from "@mui/material";

import SelfServiceServices from "../../services/v1/selfServiceServices";
import { useSnackbar } from "../../ui-components/snackbarProvider";

const RESEND_COOLDOWN_SECONDS = 60;

// Identity-checked (email + phone + emailed OTP) entry point into the
// self-service edit page. OTP here just proves ownership of the
// email/phone on file -- once verified, the donor edits freely on
// SelfServiceEdit.js for the life of the session token, not per-field.
const ChangeDetailsContent = ({ handleCloseDialog }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [step, setStep] = useState(1); // 1: identify, 2: otp, 3: pick reservation
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sessionToken, setSessionToken] = useState(null);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSendOtp = async () => {
    if (!email.trim() || !phone.trim()) {
      showSnackbar("Please enter your email and phone number.", { severity: "warning" });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await SelfServiceServices.requestDetailsOtp({ email: email.trim(), phone: phone.trim() });
      showSnackbar(response.message || "A verification code has been sent.", { severity: "success" });
      setStep(2);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      showSnackbar(err.message || "Something went wrong. Please try again.", { severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToEdit = (donorId, token) => {
    sessionStorage.setItem("selfServiceToken", token);
    sessionStorage.setItem("selfServiceDonorId", donorId);
    handleCloseDialog();
    navigate("/manage-reservation");
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      showSnackbar("Please enter the verification code from your email.", { severity: "warning" });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await SelfServiceServices.verifyDetailsOtp({
        email: email.trim(),
        phone: phone.trim(),
        otpCode: otpCode.trim(),
      });
      const { token, reservations: matchedReservations } = response.data || {};
      if (!token || !matchedReservations || matchedReservations.length === 0) {
        showSnackbar("No upcoming reservations found for this account.", { severity: "warning" });
        return;
      }

      setSessionToken(token);
      if (matchedReservations.length === 1) {
        goToEdit(matchedReservations[0].donor_id, token);
      } else {
        setReservations(matchedReservations);
        setStep(3);
      }
    } catch (err) {
      showSnackbar(err.message || "Invalid or expired code.", { severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {step === 1 && (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the email and phone number you booked with. We'll email you
            a verification code to confirm it's you.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone Number"
            placeholder="7XXXXXXXX"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").replace(/^0+/, ""))}
            InputProps={{
              startAdornment: <InputAdornment position="start">+94</InputAdornment>,
            }}
            fullWidth
            margin="normal"
          />
          <Button
            variant="contained"
            sx={{ bgcolor: "#8B2757", mt: 2 }}
            fullWidth
            onClick={handleSendOtp}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Send Verification Code"}
          </Button>
        </>
      )}

      {step === 2 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            We've <strong>emailed</strong> a verification code to <strong>{email}</strong>.
            Check your inbox (and spam folder) and enter it below.
          </Alert>
          <TextField
            label="Verification Code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 6, inputMode: "numeric" }}
          />
          <Button
            variant="contained"
            sx={{ bgcolor: "#8B2757", mt: 2 }}
            fullWidth
            onClick={handleVerifyOtp}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Verify"}
          </Button>
          <Button
            variant="text"
            fullWidth
            sx={{ mt: 1 }}
            onClick={handleSendOtp}
            disabled={isSubmitting || cooldown > 0}
          >
            {cooldown > 0 ? `Resend OTP (${cooldown}s)` : "Resend OTP"}
          </Button>
        </>
      )}

      {step === 3 && (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            We found more than one upcoming reservation. Which one would you
            like to update?
          </Typography>
          <List>
            {reservations.map((r) => (
              <ListItemButton
                key={r.reservation_id}
                onClick={() => goToEdit(r.donor_id, sessionToken)}
                sx={{ border: "1px solid lightgrey", borderRadius: 1, mb: 1 }}
              >
                <ListItemText
                  primary={`${r.reservation_number} - ${r.package_title || ""}`}
                  secondary={r.date}
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default ChangeDetailsContent;
